import { Autorenew, People, Schedule } from "@mui/icons-material";
import { Box, Grid, Portal, Switch, Typography } from "@mui/material";
import { ClickableTooltip } from "Components/ClickableTooltip";
import { StyledHelp } from "Components/ClickableTooltip/StyledHelp";
import { StyledFlexBox } from "Components/UI/StylesFlexBox";
import { AnalyticsSpacing, PageActionPreSlot } from "consts";
import StandardAnalyticsChartStatCard from "features/analytics/components/StandardAnalyticsChartStatCard";
import {
  CHART_KEY_STORE_CUSTOMER_RETENTION_REPEAT_CUSTOMER_RATE,
  CHART_KEY_STORE_CUSTOMER_RETENTION_REPEAT_PURCHASE_RATE,
  CHART_KEY_STORE_CUSTOMER_RETENTION_TIME_TO_SECOND_ORDER,
} from "features/analytics/constants/chartKeys";
import {
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
} from "@incendium/api";
import {
  CustomerRetentionCohortProvider,
  CustomerRetentionTable,
  RetentionMetricsOverTime,
  RepeatCustomerAvgOrdersStatCard,
  TimeToSecondOrderDistribution,
  useCustomerRetentionCohort,
} from "features/analytics";
import { useMemo } from "react";
import { table10Rows } from "features/analytics";

function CustomerRetentionCohortToggle() {
  const { closedCohort, setClosedCohort } = useCustomerRetentionCohort();

  return (
    <Portal container={() => document.getElementById(PageActionPreSlot)}>
      <StyledFlexBox>
        <Box display="flex" alignItems="center" mr={1}>
          <Switch
            checked={closedCohort}
            onChange={(_, checked) => setClosedCohort(checked)}
          />
          <Typography
            fontWeight={600}
            color={closedCohort ? "primary" : "secondary"}
            noWrap
          >
            Closed Cohort
          </Typography>
        </Box>
        <ClickableTooltip
          placement="bottom"
          text={
            <>
              <p>
                <strong>Closed (cohort)</strong> — Customers whose first
                purchase was in the selected period. Stat cards show their
                behaviour across all time (repeat rates, avg orders, time to
                second order).
              </p>
              <p>
                <strong>Open (activity)</strong> — Activity in the selected
                period only. Stat cards reflect customers who purchased or had
                sessions in that window.
              </p>
            </>
          }
          icon={<StyledHelp fontSize="small" />}
        />
      </StyledFlexBox>
    </Portal>
  );
}

function RepeatCustomerRateStatCard() {
  const { dateDimension } = useCustomerRetentionCohort();

  const chart = useMemo(
    () => ({
      name: "Repeat Customer Rate",
      dimension: [ApiDimension.DIMENSION_NOT_SET],
      template: ApiChartTemplate.SNAPSHOT,
      type: ApiChartType.STAT_CARD,
      dateDimension,
      attributes: [],
      yAxisKeys: [
        {
          key: "l",
          fields: [ApiMetric.METRIC_REPEAT_CUSTOMER_RATE],
        },
      ],
      displayOptions: {
        statTitle: "Repeat Customer Rate",
        statDesignIndex: 1,
      },
      comparison: true,
    }),
    [dateDimension],
  );

  return (
    <StandardAnalyticsChartStatCard
      chartKey={CHART_KEY_STORE_CUSTOMER_RETENTION_REPEAT_CUSTOMER_RATE}
      icon={<People />}
      chart={chart}
    />
  );
}

function TimeToSecondOrderStatCard() {
  const { dateDimension } = useCustomerRetentionCohort();

  const chart = useMemo(
    () => ({
      name: "Time to Second Order",
      dimension: [ApiDimension.DIMENSION_NOT_SET],
      template: ApiChartTemplate.SNAPSHOT,
      type: ApiChartType.STAT_CARD,
      dateDimension,
      attributes: [],
      yAxisKeys: [
        {
          key: "l",
          fields: [ApiMetric.METRIC_AVERAGE_DAYS_TO_SECOND_PURCHASE],
        },
      ],
      displayOptions: {
        statTitle: "Time to Second Order",
        statDesignIndex: 2,
      },
      comparison: true,
    }),
    [dateDimension],
  );

  return (
    <StandardAnalyticsChartStatCard
      chartKey={CHART_KEY_STORE_CUSTOMER_RETENTION_TIME_TO_SECOND_ORDER}
      icon={<Schedule />}
      chart={chart}
    />
  );
}

function RepeatPurchaseRateStatCard() {
  const { dateDimension } = useCustomerRetentionCohort();

  const chart = useMemo(
    () => ({
      name: "Repeat Purchase Rate",
      dimension: [ApiDimension.DIMENSION_NOT_SET],
      template: ApiChartTemplate.SNAPSHOT,
      type: ApiChartType.STAT_CARD,
      dateDimension,
      attributes: [],
      yAxisKeys: [
        {
          key: "l",
          fields: [ApiMetric.METRIC_REPEAT_PURCHASE_RATE],
        },
      ],
      displayOptions: {
        statTitle: "Repeat Purchase Rate",
        statDesignIndex: 4,
      },
      comparison: true,
    }),
    [dateDimension],
  );

  return (
    <StandardAnalyticsChartStatCard
      chartKey={CHART_KEY_STORE_CUSTOMER_RETENTION_REPEAT_PURCHASE_RATE}
      icon={<Autorenew />}
      chart={chart}
    />
  );
}

function StoreCustomerRetentionPageContent() {
  return (
    <Grid container spacing={AnalyticsSpacing} mb={4}>
      <Grid item xs={12} sm={6} md={3}>
        <RepeatCustomerRateStatCard />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <TimeToSecondOrderStatCard />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <RepeatCustomerAvgOrdersStatCard />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <RepeatPurchaseRateStatCard />
      </Grid>

      <Grid item xs={7} sx={{ minHeight: 495 }}>
        <RetentionMetricsOverTime />
      </Grid>
      <Grid item xs={5} sx={{ minHeight: 495 }}>
        <TimeToSecondOrderDistribution />
      </Grid>

      <Grid item xs={12} sx={{ minHeight: table10Rows }}>
        <CustomerRetentionTable />
      </Grid>
    </Grid>
  );
}

function StoreCustomerRetentionPage() {
  return (
    <CustomerRetentionCohortProvider>
      <CustomerRetentionCohortToggle />
      <StoreCustomerRetentionPageContent />
    </CustomerRetentionCohortProvider>
  );
}

export default StoreCustomerRetentionPage;
